<template>
  <modal ref="modal"
         :modal-id="`${entityType}-${fieldName}-upload-modal`"
         :paddingless-body="uploading"
         :click-outside-to-close="!cropping"
         @visibility-changed="visibilityChanged">
    <template #body>
      <alert v-if="errorMessage" :message="errorMessage" />

      <image-source-selector v-if="selectingSource" :description="description"
                             @upload-file="showUploadSource" @search-image="searchImage" />

      <search-stock-image v-if="selectingStockImage" :description="description" :search-query="stockImageSearchQuery"
                          :external-image-attribution-utm-params="externalImageAttributionUtmParams"
                          :external-image-photo-retrieval-url-with-placeholder="externalImagePhotoRetrievalUrlWithPlaceholder"
                          :external-image-search-url="externalImageSearchUrl"
                          @use-stock-image="useStockImage" />

      <template v-if="uploading">
        <div class="d-flex flex-column align-items-center mb-4">
          <div class="padded-content d-flex flex-column justify-content-center align-items-center">
            <strong class="title mb-2">
              {{ i18n.t('components.image_upload.upload_title') }}
            </strong>
            <div class="text-center">
              {{ description }}
            </div>
          </div>
        </div>
        <direct-dropzone ref="dropzone" class="d-none d-md-flex justify-content-center align-items-center flex-column"
                         :title="i18n.t('components.dropzone.image.title')"
                         :description="modalDescription"
                         :disclaimer="i18n.t('components.dropzone.image.disclaimer')"
                         :param-name="`${entityType}[${fieldName}]`"
                         :accepted-file-types="imageFileTypes"
                         :upload-url="imageBaseUrl"
                         @upload-success="finishedUpload" @upload-error="setUploadErrorMessage" />
        <p v-if="uploadErrorMessage" class="text-danger text-center">
          {{ uploadErrorMessage }}
        </p>
        <div class="mt-4 padded-content">
          <modal-button class="btn-primary" type="button" @click="openDropzone">
            {{ i18n.t('components.image_upload.choose_file') }}
          </modal-button>
          <modal-button class="btn-light mt-3" type="button" @click="close">
            {{ i18n.t('administrative.cancel') }}
          </modal-button>
        </div>
      </template>
      <template v-if="cropping">
        <div class="d-flex flex-column justify-content-center align-items-center">
          <strong class="title mb-2">
            {{ i18n.t('components.image_upload.crop_title') }}
          </strong>
          <p class="mt10 text-center">
            {{ i18n.t('components.image_upload.cropping_instructions') }}
          </p>
        </div>
        <cropper :original-image-url="image.originalUrl" :aspect-ratio="aspectRatio" :enforce-aspect-ratio="true"
                 @update="updateCrop" />
        <div class="mt-3">
          <modal-button class="btn-primary" type="button" :label="i18n.t('buttons.crop')" @click="crop">
            {{ i18n.t('buttons.crop') }}
          </modal-button>
          <modal-button class="btn-light mt-3" type="button" :label="i18n.t('components.image_upload.skip_crop')" @click="stopCropping">
            {{ i18n.t('components.image_upload.skip_crop') }}
          </modal-button>
        </div>
      </template>
    </template>
  </modal>
</template>

<script>
import ajax from '../helpers/ajax'
import Alert from '@/components/alert'
import Cropper from './cropper'
import DirectDropzone from '@/components/upload/direct-dropzone'
import { IMAGE_FILE_TYPES } from '@/components/upload/file-types'
import ImageSourceSelector from './image-source-selector'
import Modal from '../components/modal'
import ModalButton from '../modals/modal-button'
import { nextTick } from 'vue'
import { objectKeysToSnakeCase } from '@/helpers/transform-object-keys'
import SearchStockImage from './search-stock-image'

export default {
  components: { Alert, Cropper, DirectDropzone, ImageSourceSelector, Modal, ModalButton, SearchStockImage },

  props: {
    allowCropping: {
      type: Boolean,
      required: false,
      default: true
    },
    allowExternalImage: {
      type: Boolean,
      required: true
    },
    aspectRatio: {
      type: Number,
      required: true
    },
    image: {
      type: Object,
      required: false
    },
    description: {
      type: String,
      required: false
    },
    entityType: {
      type: String,
      required: true
    },
    externalImageAttributionUtmParams: {
      type: Object,
      required: false
    },
    externalImagePhotoRetrievalUrlWithPlaceholder: {
      type: String,
      required: false
    },
    externalImageSearchUrl: {
      type: String,
      required: false
    },
    fieldName: {
      type: String,
      required: true
    },
    imageBaseUrl: {
      type: String,
      required: true
    },
    previewStyle: {
      type: String,
      required: false,
      default: 'form'
    }
  },

  emits: ['done-cropping', 'image-updated', 'visibility-changed'],

  data() {
    return {
      cropRect: null,
      errorMessage: null,
      stage: null,
      stockImageSearchQuery: '',
      uploadErrorMessage: null
    }
  },

  computed: {
    cropping() {
      return this.stage === 'cropping'
    },

    cropWhxy() {
      return `${this.cropRect.w}x${this.cropRect.h}+${this.cropRect.x}+${this.cropRect.y}`
    },

    imageFileTypes() {
      return IMAGE_FILE_TYPES
    },

    modalSize() {
      if (this.cropping) {
        return 'lg'
      } else {
        return 'md'
      }
    },

    modalTitle() {
      if (this.cropping) {
        return this.i18n.t('buttons.crop')
      } else {
        if (this.image) {
          return this.i18n.t('components.image_upload.edit')
        } else {
          return this.i18n.t('components.image_upload.new')
        }
      }
    },

    modalDescription() {
      // Build the string with the list of supported file types
      const supportedFileTypes = ['.jpg', '.png', '.gif', '.heic', '.heif', '.avif', '.tiff']
      const fileTypesString = `${supportedFileTypes.slice(0, -1).join(', ')} ${this.i18n.t('or')} ${supportedFileTypes.slice(-1)}`

      return this.i18n.t('components.dropzone.accepted_file_types', {file_types: fileTypesString})
    },

    selectingSource() {
      return this.stage === 'selectingSource'
    },

    selectingStockImage() {
      return this.stage === 'selectingStockImage'
    },

    uploading() {
      return this.stage === 'uploading'
    }
  },

  methods: {
    openDropzone() {
      // First, clear out any previous upload attempt
      this.$refs.dropzone.clearFile()
      this.uploadErrorMessage = null

      // See: https://stackoverflow.com/questions/49197488/how-to-manually-trigger-click-for-dropzone-open-select-file-dialog
      // It seems there is no cleaner way to use an element outside the dropzone to trigger the dialog to
      // open as if the user clicked the dropzone.
      this.$refs.dropzone.$el.click()
    },
    crop() {
      ajax.patch({
        url: `${this.imageBaseUrl}/crop`,
        data: {
          id: this.fieldName,
          [this.entityType]: {
            crop_whxy: this.cropWhxy
          }
        },
        successHandler: (response) => {
          this.errorMessage = null
          this.$refs.modal.close()
          this.updateImage({
            originalUrl: response.imageUrls.original,
            previewUrl: response.imageUrls[this.previewStyle],
            allUrls: response.imageUrls,
            type: 'uploaded-image'
          })
          this.stopCropping()
        },
        errorHandler: ({ message }) => {
          this.errorMessage = message
        }
      })
    },

    finishedUpload({image_urls}) {
      this.updateImage({
        originalUrl: image_urls.original,
        previewUrl: image_urls[this.previewStyle],
        allUrls: image_urls,
        type: 'uploaded-image'
      })
      if (this.allowCropping) {
        nextTick(() => {
          this.startCropping()
        })
      } else {
        this.$refs.modal.close()
        this.stage = null
      }
    },

    open() {
      this.$refs.modal.open()

      if (this.allowExternalImage) {
        this.stage = 'selectingSource'
      }
      else {
        this.stage = 'uploading'
      }
    },

    close() {
      this.$refs.modal.close()
    },

    searchImage(imageQuery) {
      this.stage = 'selectingStockImage'
      this.stockImageSearchQuery = imageQuery
    },

    setUploadErrorMessage({ message }) {
      this.uploadErrorMessage = message
    },

    showUploadSource() {
      this.stage = 'uploading'
    },

    startCropping() {
      this.stage = 'cropping'
    },

    stopCropping() {
      this.$refs.modal.close()
      this.cropRect = null
      this.stage = null
      this.$emit('done-cropping')
    },

    useStockImage(stockImageProperties) {
      const attributionData = objectKeysToSnakeCase(stockImageProperties.user)

      ajax.post({
        url: `${this.imageBaseUrl}/external`,
        data: {
          image: {
            external_url: stockImageProperties.originalImageUrl,
            external_id: stockImageProperties.id,
            attribution_data: attributionData
          }
        },
        successHandler: (response) => {
          this.updateImage({
            originalUrl: response.imageUrls.original,
            previewUrl: response.imageUrls[this.previewStyle],
            allUrls: response.imageUrls,
            attributionData: stockImageProperties.user,
            type: 'external-image'
          })

          // Unsplash doesn't support cropping images, so we skip that
          this.stopCropping()
        }
      })
    },

    updateCrop({x, y, height, width}) {
      this.cropRect = {w: Math.round(width),
                       h: Math.round(height),
                       x: Math.round(x),
                       y: Math.round(y)}
    },

    updateImage(image) {
      this.$emit('image-updated', image)
    },

    visibilityChanged(newVisibility) {
      this.$emit('visibility-changed', newVisibility)
    }
  }
}
</script>

<style lang="scss" scoped>
  .title {
    font-size: 24px;
  }

  .padded-content {
    padding: 0 20px;
  }
</style>
